import { Link } from "react-router-dom";
import { ArrowRight, Compass, Globe, MousePointerClick, Search, Wrench, ClipboardList, CalendarCheck, MessagesSquare, Route } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { useLanguage } from "../i18n";

const TOPICS = [
  { icon: Globe, title: "Strategia per la presenza online", desc: "Dove essere, come presentarti e quali canali hanno senso per la tua attività." },
  { icon: Wrench, title: "Miglioramento del sito web", desc: "Analisi del tuo sito attuale e interventi concreti per renderlo più efficace." },
  { icon: MousePointerClick, title: "Esperienza utente e conversioni", desc: "Percorsi più semplici per chi visita il sito e più opportunità di contatto." },
  { icon: Search, title: "Posizionamento online e SEO", desc: "Per farti trovare da chi cerca i tuoi servizi sui motori di ricerca." },
  { icon: Compass, title: "Scelta degli strumenti digitali", desc: "Gli strumenti giusti per lavorare meglio, senza complicazioni inutili." },
  { icon: ClipboardList, title: "Analisi delle esigenze aziendali", desc: "Un quadro chiaro delle priorità digitali della tua impresa." },
];

const STEPS = [
  { icon: CalendarCheck, title: "Prenota", desc: "Scegli il tipo di consulenza, il giorno e l'orario che preferisci." },
  { icon: MessagesSquare, title: "Confrontiamoci", desc: "Parliamo della tua attività, dei tuoi obiettivi e dei tuoi dubbi." },
  { icon: Route, title: "Pianifica", desc: "Ricevi un orientamento chiaro sui possibili prossimi passi, senza impegno." },
];

export default function Consulenze() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={`${t("Consulenza digitale personalizzata")} — Alsera Studio`}
        description={t("Ti aiutiamo a individuare le opportunità digitali più adatte alla tua attività, definendo una strategia chiara e sostenibile.")}
      />
      <section className="pt-32 pb-14 sm:pt-40" data-testid="consulenze-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Consulenze")}</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Una strategia chiara, costruita su di te.")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Ti aiutiamo a individuare le opportunità digitali più adatte alla tua attività, definendo una strategia chiara e sostenibile.")}
            </p>
            <Link to="/prenota" className="btn-primary mt-8" data-testid="consulenze-cta">
              {t("Prenota una consulenza")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="consulenze-temi">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl">
              {t("Di cosa possiamo parlare.")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TOPICS.map((topic, i) => (
              <Reveal key={topic.title} delay={i * 0.06}>
                <div className="group card-soft h-full p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`consulenza-tema-${i}`}>
                  <div className="mb-5 inline-flex rounded-2xl bg-acqua p-3 text-petrolio transition-colors duration-500 group-hover:bg-petrolio group-hover:text-avorio">
                    <topic.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight text-antracite">{t(topic.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(topic.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="consulenze-come">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Come funziona")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Tre passi, zero complicazioni.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div data-testid={`consulenza-step-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-3xl font-extrabold text-salvia">{String(i + 1).padStart(2, "0")}</span>
                    <s.icon className="h-5 w-5 text-petrolio" />
                  </div>
                  <h3 className="mt-3 text-lg font-bold tracking-tight text-antracite">{t(s.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(s.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/prenota" className="btn-primary mt-12" data-testid="consulenze-cta-bottom">
              {t("Prenota il tuo appuntamento")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
